import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Divider,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import TerminalIcon from "@mui/icons-material/Terminal";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";

interface IModalInstrucoesLinuxProps {
  open: boolean;
  onClose: () => void;
  onCadastrarOutra?: () => void;
  clienteNome?: string;
  clienteID?: string;
  token?: string;
  isPosCadastro?: boolean;
}

export const ModalInstrucoesLinux: React.FC<IModalInstrucoesLinuxProps> = ({
  open,
  onClose,
  onCadastrarOutra,
  clienteNome,
  clienteID,
  token,
  isPosCadastro = false
}) => {
  const theme = useTheme();
  const [copiado, setCopiado] = useState<string | null>(null);
  const [snackbarAberto, setSnackbarAberto] = useState(false);

  const tokenEfetivo = token || "SEU_TOKEN_AQUI";
  const clienteIDEfetivo = clienteID || "ID_DO_CLIENTE";

  const comandoDownload = "curl -fsSL https://srvbkp.backupfacil.com/api/v2/downloads/linux/instalar.sh -o instalar.sh";
  const comandoInstalar = `sudo bash instalar.sh --token ${tokenEfetivo} --cliente ${clienteIDEfetivo}`;
  const comandoOneLiner = `curl -fsSL https://srvbkp.backupfacil.com/api/v2/downloads/linux/instalar.sh | sudo bash -s -- --token ${tokenEfetivo} --cliente ${clienteIDEfetivo}`;

  const handleCopiar = (texto: string, label: string) => {
    navigator.clipboard.writeText(texto);
    setCopiado(label);
    setSnackbarAberto(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 24,
            backgroundImage: "none"
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: "primary.main",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <TerminalIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {isPosCadastro ? "Tarefa Salva! Instruções do Agente Linux (Ubuntu)" : "Instalação do Agente Backup Fácil — Linux (Ubuntu)"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Execute os dois comandos abaixo no terminal do servidor para conectar o agente automaticamente.
              </Typography>
            </Box>
            <Chip
              label="Ubuntu / Debian"
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2.5 }}>
          {isPosCadastro && (
            <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>
              A tarefa foi configurada com sucesso. Para que os backups comecem a ser gerados nos horários agendados, conclua a instalação do agente no servidor Ubuntu através dos passos abaixo.
            </Alert>
          )}

          {clienteNome && (
            <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Cliente vinculado:
              </Typography>
              <Chip label={clienteNome} size="small" variant="filled" sx={{ fontWeight: 600 }} />
            </Box>
          )}

          {/* PASSO 1 */}
          <Box sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                1
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Baixar o script de instalação oficial:
              </Typography>
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: 2,
                fontFamily: "monospace",
                borderColor: theme.palette.divider
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "'Fira Code', 'Roboto Mono', monospace",
                  fontSize: "0.88rem",
                  wordBreak: "break-all",
                  color: theme.palette.mode === "dark" ? "info.light" : "primary.dark"
                }}
              >
                {comandoDownload}
              </Typography>
              <Tooltip title={copiado === "p1" ? "Copiado!" : "Copiar comando"}>
                <IconButton
                  size="small"
                  color={copiado === "p1" ? "success" : "primary"}
                  onClick={() => handleCopiar(comandoDownload, "p1")}
                  sx={{ ml: 1 }}
                >
                  {copiado === "p1" ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Paper>
          </Box>

          {/* PASSO 2 */}
          <Box sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                2
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Executar a instalação automática com privilégios de administrador (sudo):
              </Typography>
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: 2,
                fontFamily: "monospace",
                borderColor: theme.palette.divider
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "'Fira Code', 'Roboto Mono', monospace",
                  fontSize: "0.88rem",
                  wordBreak: "break-all",
                  color: theme.palette.mode === "dark" ? "info.light" : "primary.dark"
                }}
              >
                {comandoInstalar}
              </Typography>
              <Tooltip title={copiado === "p2" ? "Copiado!" : "Copiar comando"}>
                <IconButton
                  size="small"
                  color={copiado === "p2" ? "success" : "primary"}
                  onClick={() => handleCopiar(comandoInstalar, "p2")}
                  sx={{ ml: 1 }}
                >
                  {copiado === "p2" ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Paper>
          </Box>

          {/* ACORDEÕES DE APOIO */}
          <Stack spacing={1} sx={{ mt: 3 }}>
            {/* Comando Único */}
            <Accordion variant="outlined" sx={{ borderRadius: "8px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                  <TerminalIcon fontSize="small" color="action" />
                  Opção Rápida: Comando Único (One-Liner)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Baixa e executa em uma única instrução:
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderRadius: 2
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "'Fira Code', 'Roboto Mono', monospace",
                      fontSize: "0.82rem",
                      wordBreak: "break-all"
                    }}
                  >
                    {comandoOneLiner}
                  </Typography>
                  <IconButton
                    size="small"
                    color={copiado === "oneliner" ? "success" : "primary"}
                    onClick={() => handleCopiar(comandoOneLiner, "oneliner")}
                    sx={{ ml: 1 }}
                  >
                    {copiado === "oneliner" ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Paper>
              </AccordionDetails>
            </Accordion>

            {/* Dicas PostgreSQL no Ubuntu */}
            <Accordion variant="outlined" sx={{ borderRadius: "8px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                  <StorageIcon fontSize="small" color="primary" />
                  Configuração Importante: PostgreSQL no Ubuntu (Execução sem Senha)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
                  Como as 3 tarefas diárias serão executadas automaticamente pelo serviço em segundo plano (daemon do systemd), o utilitário <code>pg_dump</code> não deve ser interrompido solicitando senha interativa.
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Opção recomendada: Criar o arquivo de senhas <code>/root/.pgpass</code>:
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
                    borderRadius: 2,
                    mb: 1
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                    echo "127.0.0.1:5432:*:postgres:SUA_SENHA_AQUI" | sudo tee /root/.pgpass &gt; /dev/null<br />
                    sudo chmod 600 /root/.pgpass
                  </Typography>
                </Paper>
                <Typography variant="caption" color="text.secondary">
                  Substitua <code>SUA_SENHA_AQUI</code> pela senha do banco de dados informada na configuração da tarefa.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* Verificação do Serviço */}
            <Accordion variant="outlined" sx={{ borderRadius: "8px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                  <SettingsIcon fontSize="small" color="action" />
                  Comandos para Gerenciamento do Serviço (systemd)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Verificar status do agente:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", color: "primary.main" }}>
                      sudo systemctl status backupfacil
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Acompanhar logs de execução em tempo real:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", color: "primary.main" }}>
                      sudo journalctl -u backupfacil -f
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Reiniciar o serviço do agente:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", color: "primary.main" }}>
                      sudo systemctl restart backupfacil
                    </Typography>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: onCadastrarOutra ? "space-between" : "flex-end" }}>
          {onCadastrarOutra && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={onCadastrarOutra}
              sx={{ fontWeight: 600 }}
            >
              Cadastrar Próxima Tarefa (ex: 2ª ou 3ª do dia)
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={onClose}
            sx={{ fontWeight: 600, px: 3 }}
          >
            {onCadastrarOutra ? "Concluir e Ir para Tarefas" : "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarAberto}
        autoHideDuration={2500}
        onClose={() => setSnackbarAberto(false)}
        message="Comando copiado para a área de transferência!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};
